import { useTranslation } from 'react-i18next'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { ExpandMore, Speed, Thermostat, Tune } from '@mui/icons-material'
import AuroraAssetGroupHeatPumpStatusControl from './AuroraAssetGroupHeatPumpStatusControl'
import AuroraAssetGroupHeatPumpTargetTemperatureControl from './AuroraAssetGroupTargetTemperatureControl'
import AuroraAssetGroupCirculationPumpStatusControl from './AuroraAssetGroupCirculationPumpStatusControl'
import AuroraAssetGroupCirculationPumpModeControl from './AuroraAssetGroupCirculationPumpModeControl'
import AuroraAssetGroupMinTemperatureLimitControl from './AuroraAssetGroupMinTemperatureLimitControl'
import AuroraAssetGroupMinPressureLimitControl from './AuroraAssetGroupMinPressureLimitControl'
import AuroraAssetGroupMinFlowLimitControl from './AuroraAssetGroupMinFlowLimitControl'
import AuroraControlModeToggle from './AuroraControlModeToggle'
import { AuroraAssetGroup, AuroraAwhpData, Sensor } from '../types'
import { AuroraControlMode } from '../enums'
import { usePolicy } from '../hooks'

interface AuroraAssetGroupControlsProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  sensors: Record<string, Sensor>
  mode?: AuroraControlMode
  onModeChange?: (mode: AuroraControlMode) => void
}

export default function AuroraAssetGroupControls({
  auroraAssetGroup,
  auroraAwhpData,
  sensors,
  mode = AuroraControlMode.STACK,
  onModeChange
}: AuroraAssetGroupControlsProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The policy hook.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The theme hook.
   */
  const theme = useTheme()

  return (
    <Accordion
      defaultExpanded
      sx={{
        background: 'transparent',
        boxShadow: 0,
        '::before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          pl: 2,
          pr: 4
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          borderRadius: 2,
          '&:not(.Mui-expanded):hover': {
            background: theme.palette.background.paper
          }
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Tune htmlColor={theme.palette.text.secondary} />
          <Typography variant="h6" color="text.secondary">
            {t('controls')}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Card>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 4 }}>
                  <Thermostat htmlColor={theme.palette.text.secondary} />
                  <Typography variant="h6">{t('heatPumpControl')}</Typography>
                </Stack>

                <Stack direction="column" gap={2}>
                  <AuroraAssetGroupHeatPumpStatusControl auroraAssetGroup={auroraAssetGroup} />

                  <AuroraAssetGroupHeatPumpTargetTemperatureControl
                    auroraAssetGroup={auroraAssetGroup}
                    auroraAwhpData={auroraAwhpData}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 4 }}>
                  <Speed htmlColor={theme.palette.text.secondary} />
                  <Typography variant="h6">{t('circulationPumpControl')}</Typography>
                </Stack>

                <Stack direction="column" gap={2}>
                  <AuroraAssetGroupCirculationPumpStatusControl
                    auroraAssetGroup={auroraAssetGroup}
                  />

                  <AuroraAssetGroupCirculationPumpModeControl auroraAssetGroup={auroraAssetGroup} />
                </Stack>
              </Grid>

              {isSuperadmin && (
                <Grid item xs={12} sx={{ pt: '8px !important' }}>
                  <Accordion
                    sx={{
                      background: 'transparent',
                      boxShadow: 0,
                      '::before': { display: 'none' },
                      '& .MuiAccordionSummary-root': {
                        pl: 2,
                        pr: 4
                      },
                      '& .MuiAccordionSummary-content': {
                        m: 0
                      }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" alignItems="center" gap={2}>
                        <Tune htmlColor={theme.palette.text.secondary} />
                        <Typography variant="h6">{t('advancedControls')}</Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={4}>
                        <Grid item xs={12} lg={4}>
                          <AuroraAssetGroupMinTemperatureLimitControl
                            auroraAssetGroup={auroraAssetGroup}
                          />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                          <AuroraAssetGroupMinPressureLimitControl
                            auroraAssetGroup={auroraAssetGroup}
                          />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                          <AuroraAssetGroupMinFlowLimitControl
                            auroraAssetGroup={auroraAssetGroup}
                          />
                        </Grid>

                        {onModeChange && (
                          <Grid item xs={12}>
                            <Stack direction="row" justifyContent="center" alignItems="center">
                              <AuroraControlModeToggle mode={mode} onModeChange={onModeChange} />
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </AccordionDetails>
    </Accordion>
  )
}
